import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import BufferZoneHeader from "../components/BufferZoneHeader";
import EventBufferTable from "../components/EventBufferTable";
import EmergencyV0 from "../emergencyV0";
import SelectionSummaryPanel from "../components/SelectionSummaryPanel";
import useBufferFilters from "../hooks/useBufferFilters";
import {
  bufferSortingOptions,
  bufferTimeOptions,
  defaultBufferFilters,
  mockBufferEvents,
} from "../constants";
import bufferZoneService from "../../../services/bufferZoneService";
import { EventFiltersBar } from "../../../components";
import SiteSelectionDialog from "../../../components/SiteSelectionDialog";
import { setToastMessage } from "../../../reducers/common.reducer";
import {
  FaCloudDownloadAlt,
  FaExclamationTriangle,
  FaRegTimesCircle,
} from "react-icons/fa";
import { useWindowWidth } from "@react-hook/window-size";
import { SaveMultipleBettingSites } from "../hooks/apiFetcher";
//import Alert from "@mui/material/Alert";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import DotLottieLoading from "../../../components/Animations/DotLottieLoading";
import { useLogTrail } from "../../../hooks/useLogTrail";
import { useSessionStorage } from "../../../hooks/useSessionStorage";

const cleanText = (value) => {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
};

const mapSelectionFromEvents = (events = [], previous = {}) => {
  const next = {};

  events.forEach((event) => {
    if (!event?.id) {
      return;
    }
    const eventId = String(event.id);
    const existing = previous[eventId];
    if (existing) {
      next[eventId] = { ...existing };
      return;
    }

    const autoSingle = Boolean(event.singleEligible);
    const autoParlay = Boolean(event.parlayEligible);

    if (autoSingle || autoParlay) {
      next[eventId] = {
        single: autoSingle,
        parlay: autoParlay,
      };
    }
  });

  return next;
};

const PAGE_SIZE = 5;

const SITE_OPTIONS = [
  {
    id: "lc7",
    label: "Lucky Seven",
    description: "Official betting partner for live odds.",
    //lock: true
  },
  {
    id: "mnb",
    label: "Money Ball",
    description: "Sportsbook platform for web and mobile betting apps.",
  },
  {
    id: "777",
    label: "Sport 777",
    description: "High-performance sportsbook for real-time betting.",
    lock: true,
  },
  {
    id: "ep",
    label: "Sport EP",
    description: "Enterprise-grade sportsbook solution for operators.",
  },
];

const parseEligibilityFlag = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalised = value.trim().toLowerCase();
    if (!normalised) return false;
    return ["1", "true", "yes", "y", "enabled"].includes(normalised);
  }
  return false;
};

const extractNumericValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = trimmed.match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const numeric = Number(match[0]);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const determineTrend = (previous, next) => {
  console.log("determineTrend", previous, next);
  if (!Number.isFinite(previous) || !Number.isFinite(next)) {
    return "stable";
  }
  if (next > previous) return "rising";
  if (next < previous) return "dropping";
  return "stable";
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const deriveBadges = (filters) => {
  const badges = [];
  if (
    filters.advanced?.handicapTrend &&
    filters.advanced.handicapTrend !== "all"
  ) {
    badges.push(
      filters.advanced.handicapTrend === "rising"
        ? "Rising handicaps"
        : filters.advanced.handicapTrend === "dropping"
          ? "Dropping handicaps"
          : "Stable lines",
    );
  }
  if (filters.advanced?.oddsDelta) {
    badges.push(`Odds delta >= ${filters.advanced.oddsDelta}`);
  }
  if (filters.advanced?.goalLine && filters.advanced.goalLine !== "any") {
    badges.push(
      {
        high: "High goal lines",
        balanced: "Balanced goal lines",
        low: "Low goal lines",
      }[filters.advanced.goalLine] ?? "Goal line filter",
    );
  }
  if (
    filters.advanced?.updatedWithin &&
    filters.advanced.updatedWithin !== "any"
  ) {
    badges.push(`Updated ${filters.advanced.updatedWithin}`);
  }
  return badges;
};

const normalizeTeamName = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim().toLowerCase();
};

const buildMatchKey = (event = {}) => {
  const home = normalizeTeamName(event?.hname ?? event?.homeTeam);
  const away = normalizeTeamName(event?.aname ?? event?.awayTeam);
  if (!home || !away) {
    return null;
  }
  return [home, away].sort().join("|");
};

const resolveEventIdentifier = (event = {}, index = 0) => {
  const resolvedId =
    event?.eventId ??
    event?.upcommingEventId ??
    event?.upcomingEventId ??
    event?.rapidEventId ??
    event?.matchId ??
    event?.id ??
    null;

  if (resolvedId !== null && resolvedId !== undefined && resolvedId !== "") {
    return String(resolvedId);
  }

  const fallbackLeague = event?.leagueId ?? "league";
  const fallbackHome = event?.hname ?? event?.homeTeam ?? "home";
  const fallbackAway = event?.aname ?? event?.awayTeam ?? "away";
  return `${fallbackLeague}-${fallbackHome}-${fallbackAway}-${index}`;
};

const findDuplicateMatches = (array = []) => {
  const seenMatches = new Map();
  const seenIds = new Map();
  const duplicateIdentifiers = new Set();
  const summary = {
    sameNameDifferentId: 0,
    sameNameSameId: 0,
    differentNameSameId: 0,
  };

  array.forEach((item, index) => {
    const eventIdentifier = resolveEventIdentifier(item, index);
    const matchKey = buildMatchKey(item);

    if (matchKey) {
      const entries = seenMatches.get(matchKey) ?? [];
      if (entries.length) {
        const hasSameId = entries.some((entry) => entry === eventIdentifier);
        if (hasSameId) {
          summary.sameNameSameId += 1;
        } else {
          summary.sameNameDifferentId += 1;
        }
        entries.forEach((entry) => duplicateIdentifiers.add(entry));
        duplicateIdentifiers.add(eventIdentifier);
      }
      entries.push(eventIdentifier);
      seenMatches.set(matchKey, entries);
    }

    const idEntry = seenIds.get(eventIdentifier);
    if (idEntry) {
      if (matchKey) {
        const hasDifferentMatch =
          idEntry.matchKeys.size > 0 && !idEntry.matchKeys.has(matchKey);
        if (hasDifferentMatch) {
          summary.differentNameSameId += 1;
        }
        idEntry.matchKeys.add(matchKey);
      }
      idEntry.count += 1;
      duplicateIdentifiers.add(eventIdentifier);
    } else {
      const initialMatchKeys = new Set();
      if (matchKey) {
        initialMatchKeys.add(matchKey);
      }
      seenIds.set(eventIdentifier, {
        count: 1,
        matchKeys: initialMatchKeys,
      });
    }
  });

  return {
    duplicateIdentifiers,
    summary,
  };
};

const validateOdds = (events) => {
  const isMissingOdds = (value) => {
    const normalized = cleanText(value);
    return normalized === null || normalized.toUpperCase() === "N/A";
  };

  return events.filter(a => a.single || a.parlay).some((event) => {
    const manualOdds = event.emergencyOdds ?? {};
    return (
      (isMissingOdds(manualOdds.homeOdds) &&
        isMissingOdds(manualOdds.awayOdds)) ||
      isMissingOdds(manualOdds.goalOdds)
    );
  });
};

const buildEmergencyPayload = (event) => {
  const manualOdds = event.emergencyOdds ?? {};
  const homeOdds = cleanText(manualOdds.homeOdds);
  const awayOdds = cleanText(manualOdds.awayOdds)
  return {
    rapidEventId: Number(event.id),
    leagueId: event.leagueId,
    lname: event.leagueName,
    date: event.kickoff,
    eventDate: event.eventDate,
    hname: event.homeTeam,
    aname: event.awayTeam,
    homeId: event.homeTeamId,
    awayId: event.awayTeamId,
    overId:  homeOdds === null || homeOdds === "N/A" ? event.awayTeamId :  event.homeTeamId,
    underId: homeOdds === null || homeOdds === "N/A" ? event.homeTeamId :  event.awayTeamId,
    isSingle: Boolean(event.single),
    isMix: Boolean(event.parlay),
    isFivePercentCom: Boolean(event.fivePercentEligible),
    bodyhandicap: "0.0",
    goalhandicap: "0.0",
    homeodds: 0.00,
    awayodds: 0.00,
    goal: cleanText(manualOdds.goalOdds),
    body: homeOdds === null || homeOdds === "N/A" ? awayOdds : homeOdds
  };
};

const BufferZonePage = ({
  emergencyMode = false,
  onToggleEmergencyMode,
} = {}) => {
  const { readSession } = useSessionStorage();
  const userDetails = useMemo(
    () => readSession("userDetails", null),
    [readSession],
  );
  const companyInfo = useMemo(
    () => readSession("companyInfo", null),
    [readSession],
  );
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);
  const [selectionMap, setSelectionMap] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingAll, setIsUpdatingAll] = useState(false);
  const [rowFetchLoading, setRowFetchLoading] = useState({});
  const [rowSaveLoading, setRowSaveLoading] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const { logs, fetchLogs, saveLog } = useLogTrail({
    companyId: companyInfo?.companyId,
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [streamMessage, setStreamMessage] = useState("");
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [siteTargets, setSiteTargets] = useState(() =>
    SITE_OPTIONS.filter((site) => !site.lock).map((site) => site.id),
  );
  const statusPollingRef = useRef(null);
  const onlyWidth = useWindowWidth();
  const [view, setIsView] = useState("column");
  const [saveDialog, setSaveDialog] = useState(false);
  // const [message, setMessage] = useState({
  //   text: "",
  //   type: "warning",
  // });
  const [duplicateSummary, setDuplicateSummary] = useState({
    sameNameDifferentId: 0,
    sameNameSameId: 0,
    differentNameSameId: 0,
  });
  const [duplicateCount, setDuplicateCount] = useState(0);

  const notifyEmergencyMode = useCallback(() => {
    dispatch(
      setToastMessage({
        type: "warning",
        message:
          "Emergency mode is enabled. Automated handicap updates and manual row actions are disabled.",
      }),
    );
  }, [dispatch]);

  const applyDuplicateSummary = useCallback((summary = {}) => {
    const normalised = {
      sameNameDifferentId: summary?.sameNameDifferentId ?? 0,
      sameNameSameId: summary?.sameNameSameId ?? 0,
      differentNameSameId: summary?.differentNameSameId ?? 0,
    };
    setDuplicateSummary(normalised);
    setDuplicateCount(
      normalised.sameNameDifferentId +
        normalised.sameNameSameId +
        normalised.differentNameSameId,
    );
  }, []);

  const {
    filters,
    updateFilters,
    updateAdvancedFilters,
    resetFilters,
    filteredEvents,
    derived,
  } = useBufferFilters(events, defaultBufferFilters);

  const advancedFilters = filters.advanced ?? defaultBufferFilters.advanced;

  const filtersSignature = useMemo(
    () =>
      JSON.stringify({
        search: filters.search,
        league: filters.league,
        timeWindow: filters.timeWindow,
        sort: filters.sort,
        advanced: {
          handicapTrend: advancedFilters.handicapTrend,
          oddsDelta: advancedFilters.oddsDelta,
          goalLine: advancedFilters.goalLine,
          updatedWithin: advancedFilters.updatedWithin,
        },
      }),
    [
      filters.search,
      filters.league,
      filters.timeWindow,
      filters.sort,
      advancedFilters.handicapTrend,
      advancedFilters.oddsDelta,
      advancedFilters.goalLine,
      advancedFilters.updatedWithin,
    ],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [emergencyMode, filtersSignature]);

  const visibleEvents = emergencyMode ? events : filteredEvents;
  const totalItems = visibleEvents.length;

  useEffect(() => {
    setCurrentPage((previous) => {
      if (totalItems === 0) {
        return 1;
      }
      const maxPage = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
      return previous > maxPage ? maxPage : previous;
    });
  }, [totalItems]);

  const totalPages = totalItems ? Math.ceil(totalItems / PAGE_SIZE) : 0;
  const safePage = totalPages ? Math.min(currentPage, totalPages) : 1;
  const startIndex = totalItems ? (safePage - 1) * PAGE_SIZE + 1 : 0;
  const endIndex = totalItems
    ? Math.min(startIndex + PAGE_SIZE - 1, totalItems)
    : 0;

  const paginatedEvents = useMemo(() => {
    if (!totalItems) {
      return [];
    }
    const start = (safePage - 1) * PAGE_SIZE;
    return visibleEvents.slice(start, start + PAGE_SIZE);
  }, [safePage, totalItems, visibleEvents]);

  const pagination = useMemo(
    () => ({
      startIndex,
      endIndex,
      totalItems,
      currentPage: totalItems ? safePage : 0,
      totalPages,
      onPageChange: setCurrentPage,
    }),
    [startIndex, endIndex, totalItems, safePage, totalPages],
  );

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const now = new Date();
      const response = await bufferZoneService.getEvents();
      const payload = response?.data?.bufferdata ?? response?.data ?? [];

      const dataset = Array.isArray(payload) && payload.length ? payload : [];
      const { duplicateIdentifiers, summary: duplicateSummaryResult } =
        findDuplicateMatches(dataset);
      const formattedData = dataset.map((v, index) => {
        const eventId = resolveEventIdentifier(v, index);

        const handicapLine = v?.bodyhandicap ? v.bodyhandicap : null;
        const goalLine = v?.goalhandicap ? v.goalhandicap : null;

        return {
          id: eventId,
          leagueId: v.leagueId ?? v.rapidLeagueId ?? `league-${index}`,
          leagueName: v.leagueName ?? v.lname ?? "Unknown league",
          leagueCountry: v.region ?? v.country ?? v.countryName ?? "Football",
          kickoff: v.date,
          eventDate: v.eventDate,
          homeTeam: v.hname,
          awayTeam: v.aname,
          homeTeamId: v.homeId,
          awayTeamId: v.awayId,
          overTeamId: v.overId,
          underTeamId: v.underId,
          burmeseBody: v.body,
          burmeseGoal: v.goal,
          handicap: {
            line: handicapLine,
            home:
              handicapLine !== null
                ? v.homeId === v.overId
                  ? `-${handicapLine}`
                  : handicapLine
                : null,
            away:
              handicapLine !== null
                ? v.awayId === v.overId
                  ? `-${handicapLine}`
                  : handicapLine
                : null,
          },
          homeOdds: Number.isFinite(Number(v?.homeodds))
            ? Number(v.homeodds)
            : null,
          awayOdds: Number.isFinite(Number(v?.awayodds))
            ? Number(v.awayodds)
            : null,
          goalLine,
          goalOverOdds: Number.isFinite(Number(v?.overodds))
            ? Number(v.overodds)
            : null,
          goalUnderOdds: Number.isFinite(Number(v?.underodds))
            ? Number(v.underodds)
            : null,
          handicapTrend: v.handicapTrend ?? "stable",
          goalLineTrend: v.goalLineTrend ?? "stable",
          oddsDelta: Number.isFinite(Number(v?.oddsdelta ?? v?.oddsDelta))
            ? Number(v?.oddsdelta ?? v?.oddsDelta)
            : 0,
          lastSyncedAt:
            v.updatedAt ??
            new Date(now.getTime() - 15 * 60 * 1000).toISOString(),
          parlayEligible: parseEligibilityFlag(v.isMix ?? v.parlayEligible),
          singleEligible: parseEligibilityFlag(v.isSingle ?? v.singleEligible),
          fivePercentEligible: parseEligibilityFlag(
            v.isFivePercentCom ?? v.fivePercentEligible,
          ),
          status: v.status ?? "scheduled",
          isDuplicate: duplicateIdentifiers.has(eventId),
        };
      });

      applyDuplicateSummary(duplicateSummaryResult);

      if (Array.isArray(formattedData) && formattedData.length) {
        setEvents(formattedData);
        setSelectionMap((previous) =>
          mapSelectionFromEvents(formattedData, previous),
        );
      } else {
        setEvents(mockBufferEvents);
        setSelectionMap((previous) =>
          mapSelectionFromEvents(mockBufferEvents, previous),
        );
        const mockSummary = findDuplicateMatches(mockBufferEvents);
        applyDuplicateSummary(mockSummary.summary);
        dispatch(
          setToastMessage({
            type: "info",
            message: "Live data unavailable; showing sample buffer events.",
          }),
        );
      }
    } catch (error) {
      console.error("Failed to fetch buffer zone events", error);
      setEvents(mockBufferEvents);
      setSelectionMap((previous) =>
        mapSelectionFromEvents(mockBufferEvents, previous),
      );
      setErrorMessage(
        "Unable to reach buffer API. Showing sample data for now.",
      );
      dispatch(
        setToastMessage({
          type: "error",
          message: "Buffer Zone API unreachable. Working with mock data.",
        }),
      );
      const mockSummary = findDuplicateMatches(mockBufferEvents);
      applyDuplicateSummary(mockSummary.summary);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, applyDuplicateSummary]);

  useEffect(() => {
    loadEvents();
    fetchLogs();
  }, [loadEvents,fetchLogs]);

  const eventById = useMemo(() => {
    const map = new Map();
    events.forEach((event) => {
      if (!event?.id) return;
      map.set(String(event.id), event);
    });
    return map;
  }, [events]);

  const handleToggleSelection = (eventId, type, checked) => {
    const eventKey = String(eventId);
    setSelectionMap((prev) => {
      const base = eventById.get(eventKey);
      const current = prev[eventKey] ?? {
        single: Boolean(base?.singleEligible),
        parlay: Boolean(base?.parlayEligible),
      };
      const next = {
        ...current,
        [type]: checked,
      };
      return {
        ...prev,
        [eventKey]: next,
      };
    });
  };

  const selectedEvents = useMemo(() => {
    return Object.entries(selectionMap)
      .map(([id, state]) => {
        const base = eventById.get(id);
        if (!base) return null;
        return {
          ...base,
          ...state,
        };
      })
      .filter((event) => event && (event.single || event.parlay));
  }, [selectionMap, eventById]);

  const handleRemoveSelection = (eventId) => {
    const eventKey = String(eventId);
    setSelectionMap((prev) => {
      const current = prev[eventKey] ?? { single: false, parlay: false };
      return {
        ...prev,
        [eventKey]: {
          ...current,
          single: false,
          parlay: false,
        },
      };
    });
  };

  const handleResetSelections = () => {
    setSelectionMap((prev) => {
      const cleared = {};
      events.forEach((event) => {
        if (!event?.id) return;
        const eventKey = String(event.id);
        if (prev[eventKey] || event.singleEligible || event.parlayEligible) {
          cleared[eventKey] = { single: false, parlay: false };
        }
      });
      return cleared;
    });
  };

  const handleToggleTargetSite = useCallback((siteId, checked) => {
    setSiteTargets((previous) => {
      if (checked) {
        if (previous.includes(siteId)) {
          return previous;
        }
        return [...previous, siteId];
      }
      return previous.filter((id) => id !== siteId);
    });
  }, []);

  const handleSaveSelections = async () => {
    //console.log('selected buffered events', selectedEvents)
    if (!selectedEvents.length) {
      dispatch(
        setToastMessage({
          type: "warning",
          message: "Select at least one event before saving.",
        }),
      );
      return;
    }
    setSaveDialog(false);
    setSaving(true);
    try {
      const payload = {
        savedAt: new Date().toISOString(),
        events: selectedEvents.map((event) => ({
          rapidEventId: Number(event.id),
          leagueId: event.leagueId,
          lname: event.leagueName,
          date: event.kickoff,
          eventDate: event.eventDate,
          hname: event.homeTeam,
          aname: event.awayTeam,
          homeId: event.homeTeamId,
          awayId: event.awayTeamId,
          isSingle: Boolean(event.single),
          isMix: Boolean(event.parlay),
          isFivePercentCom: Boolean(event.fivePercentEligible),
          // goalhandicap: event.handicap?.line ?? event.handicap ?? null,
          // bodyhandicap: event.handicap?.home ?? null,
          // away: event.handicap?.away ?? null,
          // trend: event.handicapTrend ?? null,
          // homeodds: event.homeOdds ?? null,
          // awayodds: event.awayOdds ?? null,
          // goalLine: event.goalLine ?? null,
          // overodds: event.goalOverOdds ?? null,
          // underodds: event.goalUnderOdds ?? null,
          // delta: event.oddsDelta ?? null,
        })),
      };

      const response = await bufferZoneService.saveSelections({
        bufferResponseLists: payload.events,
      });
      if (response?.status >= 200 && response?.status < 300) {
        dispatch(
          setToastMessage({
            type: "success",
            message: "Selections saved to betting queue.",
          }),
        );
        //handleResetSelections()
        saveLog({
          eventId: "0",
          action: "SAVE_ALL",
          title: "Buffer Zone",
          companyId: userDetails?.companyId,
        });
      } else {
        throw new Error(response?.data?.message ?? "Failed to save selections");
      }
    } catch (error) {
      console.error("Failed to save buffer selections", error);
      dispatch(
        setToastMessage({
          type: "error",
          message:
            error?.response?.data?.message ??
            error?.message ??
            "Unable to save selections. Try again shortly.",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEmergencySave = async (emergencyRows = []) => {
    if (!emergencyRows.length) {
      dispatch(
        setToastMessage({
          type: "warning",
          message: "Select at least one emergency event before saving.",
        }),
      );
      return;
    }

    const isError = validateOdds(emergencyRows);
    if (isError) {
      dispatch(
        setToastMessage({
          type: "warning",
          message: "Odds are required before saving.",
        }),
      );
      return;
    }
   
    setSaving(true)
    try {
      const payload = emergencyRows.filter(a => a.single || a.parlay).map(buildEmergencyPayload)

      const res = await bufferZoneService.updateOddsManual({bufferResponseLists: payload});
      if (res?.status !== 200 && !res?.data.success) {
        dispatch(
          setToastMessage({
            type: 'error',
            message: 'Error occured while saving!',
          }),
        )
        return;
      }

      const response = await bufferZoneService.saveSelections({ bufferResponseLists: payload });
      if (response?.status >= 200 && response?.status < 300) {
        dispatch(
          setToastMessage({
            type: 'success',
            message: 'Emergency odds saved successfully.',
          }),
        )
        saveLog({ eventId: "0", action: "SAVE_EMERGENCY", title: "Buffer Zone", companyId: userDetails?.companyId })
      } else {
        throw new Error(response?.data?.message ?? 'Failed to save emergency odds')
      }
    } catch (error) {
      console.error('Failed to save emergency odds', error)
      dispatch(
        setToastMessage({
          type: 'error',
          message:
            error?.response?.data?.message ??
            error?.message ??
            'Unable to save emergency odds. Try again shortly.',
        }),
      )
    } finally {
      setSaving(false)
    }
  };

  const handleOpenSiteDialog = () => {
    if (!selectedEvents.length) {
      dispatch(
        setToastMessage({
          type: "warning",
          message: "Select at least one event before opening the site list.",
        }),
      );
      return;
    }
    setSiteDialogOpen(true);
  };

  const handleCancelSiteDialog = () => {
    if (saving) {
      return;
    }
    setSiteDialogOpen(false);
  };

  const handleConfirmSiteDialog = async () => {
    if (!siteTargets?.length) {
      dispatch(
        setToastMessage({
          type: "warning",
          message: "Pick at least one site to continue.",
        }),
      );
      return;
    }
    setSiteDialogOpen(false);
    for (const siteId of siteTargets) {
      const baseUrl = siteMapURL[siteId];
      if (!baseUrl) continue;
      setSaveLoading(true);
      try {
        const res = await SaveMultipleBettingSites(baseUrl, siteId);

        if (res.status === 200) {
          dispatch(
            setToastMessage({
              type: "success",
              message: `Selections saved to site ${siteId} successfully.`,
            }),
          );
        } else {
          dispatch(
            setToastMessage({
              type: "error",
              message: `Failed to save to site ${siteId}.`,
            }),
          );
        }
      } catch (error) {
        dispatch(
          setToastMessage({
            type: "error",
            message: `Failed to save to site ${siteId}.${error.message}.`,
          }),
        );
      } finally {
        saveLog({
          eventId: "0",
          action: "SAVE_MULTIPLE",
          title: "Buffer Zone",
          companyId: userDetails?.companyId,
        });
        setSaveLoading(false);
      }
    }
  };

  const handleUpdateAll = async () => {
    if (emergencyMode) {
      notifyEmergencyMode();
      return;
    }
    setIsUpdatingAll(true);
    try {
      const ids_arr = [];

      for (const event of events) {
        ids_arr.push(Number(event.id));
      }

      // for (const event of selectedEvents) {
      //   ids_arr.push(Number(event.id));
      // }

      await bufferZoneService.updateAllHandicaps({ rapidEventId: ids_arr });
      dispatch(
        setToastMessage({
          type: "success",
          message: "Handicaps refreshed for all buffered events.",
        }),
      );
      saveLog({
        eventId: "0",
        action: "FETCH_ALL",
        title: "Buffer Zone",
        companyId: userDetails?.companyId,
      });
      startStatusPolling();
      //await loadEvents()
    } catch (error) {
      console.error("Failed to update all handicaps", error);
      dispatch(
        setToastMessage({
          type: "error",
          message: "Unable to refresh handicaps right now. Please retry later.",
        }),
      );
      stopStatusPolling();
      setIsUpdatingAll(false);
    }
    // finally {
    //   setIsUpdatingAll(false)
    // }
  };

  const handleManualUpdate = async (event) => {
    if (emergencyMode) {
      notifyEmergencyMode();
      return;
    }
    if (!event?.id) return;
    const eventKey = String(event.id);
    setRowFetchLoading((prev) => ({
      ...prev,
      [eventKey]: true,
    }));
    try {
      const response = await bufferZoneService.updateEventHandicap(event.id);
      const payload = response?.data ?? {};
      const handicapInfo = payload?.handicapInfo ?? null;
      const oddsChange = Boolean(payload?.oddsChange);
      const targetId = handicapInfo?.rapidEventId ?? event.id;

      if (handicapInfo) {
        const nextBodyLine = handicapInfo.bodyhandicap ?? null;
        const nextGoalLine = handicapInfo.goalhandicap ?? null;

        setEvents((previousEvents) =>
          previousEvents.map((item) => {
            if (String(item?.id) !== String(targetId)) {
              return item;
            }

            const currentBodyValue = extractNumericValue(
              item?.handicap?.line ?? null,
            );
            const resolvedNextBody =
              nextBodyLine ?? item?.handicap?.line ?? null;
            const nextBodyValue = extractNumericValue(resolvedNextBody);

            const currentGoalValue = extractNumericValue(
              item?.goalLine ?? null,
            );
            const nextGoalValue = extractNumericValue(
              nextGoalLine ?? item?.goalLine ?? null,
            );

            const nextHandicapTrend = oddsChange
              ? determineTrend(currentBodyValue, nextBodyValue)
              : (item?.handicapTrend ?? "stable");

            const nextGoalTrend = oddsChange
              ? determineTrend(currentGoalValue, nextGoalValue)
              : (item?.goalLineTrend ?? "stable");

            const nextHomeOdds = toNumberOrNull(handicapInfo?.homeodds);
            const nextAwayOdds = toNumberOrNull(handicapInfo?.awayodds);
            const nextOverOdds = toNumberOrNull(handicapInfo?.overodds);
            const nextUnderOdds = toNumberOrNull(handicapInfo?.underodds);

            const formatWithPreviousSign = (previousValue) => {
              if (resolvedNextBody === null || resolvedNextBody === undefined) {
                return previousValue ?? resolvedNextBody;
              }
              const baseString = String(resolvedNextBody).trim();
              if (!baseString) return previousValue ?? resolvedNextBody;
              if (previousValue === null || previousValue === undefined) {
                return baseString;
              }
              const prevString = String(previousValue).trim();
              const bareBase = baseString.replace(/^[+-]/, "");
              if (!bareBase) return baseString;
              if (prevString.startsWith("-")) {
                return `-${bareBase}`;
              }
              if (prevString.startsWith("+")) {
                return `+${bareBase}`;
              }
              return baseString;
            };

            return {
              ...item,
              handicap: {
                ...item.handicap,
                line: resolvedNextBody,
                home: formatWithPreviousSign(item?.handicap?.home),
                away: formatWithPreviousSign(item?.handicap?.away),
              },
              homeOdds: nextHomeOdds ?? item.homeOdds,
              awayOdds: nextAwayOdds ?? item.awayOdds,
              goalLine: nextGoalLine ?? item.goalLine ?? null,
              goalOverOdds: nextOverOdds ?? item.goalOverOdds,
              goalUnderOdds: nextUnderOdds ?? item.goalUnderOdds,
              burmeseBody: handicapInfo?.body ?? item.burmeseBody,
              burmeseGoal: handicapInfo?.goal ?? item.burmeseGoal,
              handicapTrend: nextHandicapTrend,
              goalLineTrend: nextGoalTrend,
              lastSyncedAt: new Date().toISOString(),
            };
          }),
        );
      }

      dispatch(
        setToastMessage({
          type: "success",
          message: `${event.homeTeam} vs ${event.awayTeam} updated.`,
        }),
      );
      //await loadEvents()
    } catch (error) {
      console.error("Failed to update handicap for event", event.id, error);
      dispatch(
        setToastMessage({
          type: "error",
          message: "Manual handicap update failed. Please try again.",
        }),
      );
    } finally {
      setRowFetchLoading((prev) => {
        const { [eventKey]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleManualSave = async (event) => {
    if (emergencyMode) {
      notifyEmergencyMode()
      return
    }
    if (!event?.id) return;
    const eventKey = String(event.id);
    setRowSaveLoading((prev) => ({
      ...prev,
      [eventKey]: true,
    }));
    try {
      const payload = {
        rapidEventId: Number(event.id),
        isMix: event.parlayEligible,
        isSingle: event.singleEligible,
      };

      const response = await bufferZoneService.saveEventHandicap(payload);

      if (response.status === 200 && response.data.code === 1) {
        saveLog({
          eventId: event.id,
          action: "SAVE_MANUAL",
          title: "Buffer Zone",
          companyId: userDetails?.companyId,
        });
        dispatch(
          setToastMessage({
            type: "success",
            message: `${event.homeTeam} vs ${event.awayTeam} saved successfully.`,
          }),
        );
      }

      if (response.data.code === 2) {
        dispatch(
          setToastMessage({
            type: "success",
            message: `${response.data.message}`,
          }),
        );
      }
    } catch (error) {
      console.error("Failed to update handicap for event", event.id, error);
      dispatch(
        setToastMessage({
          type: "error",
          message: "Manual handicap update failed. Please try again.",
        }),
      );
    } finally {
      setRowSaveLoading((prev) => {
        const { [eventKey]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleEmergencyManualSave = async (event) => {
    if (!event?.id) return;

    if (validateOdds([event])) {
      dispatch(
        setToastMessage({
          type: "warning",
          message: "Odds are required before saving.",
        }),
      );
      return;
    }

    const eventKey = String(event.id);
    setRowSaveLoading((prev) => ({ ...prev, [eventKey]: true }));
    try {
      const res = await bufferZoneService.updateOddsManual({bufferResponseLists: buildEmergencyPayload(event)});
      if (res?.status !== 200 && !res?.data.success) {
        dispatch(
          setToastMessage({
            type: 'error',
            message: 'Error occured while saving!',
          }),
        )
        return;
      }

      const response = await bufferZoneService.saveEventHandicap(
        buildEmergencyPayload(event),
      );

      if (response?.status === 200 && response?.data?.code === 1) {
        saveLog({
          eventId: event.id,
          action: "SAVE_EMERGENCY_MANUAL",
          title: "Buffer Zone",
          companyId: userDetails?.companyId,
        });
        dispatch(
          setToastMessage({
            type: "success",
            message: `${event.homeTeam} vs ${event.awayTeam} saved successfully.`,
          }),
        );
      } else if (response?.data?.code === 2) {
        dispatch(
          setToastMessage({
            type: "success",
            message: response.data.message,
          }),
        );
      } else {
        throw new Error(response?.data?.message ?? "Failed to save emergency odds");
      }
    } catch (error) {
      console.error("Failed to save emergency odds for event", event.id, error);
      dispatch(
        setToastMessage({
          type: "error",
          message:
            error?.response?.data?.message ??
            error?.message ??
            "Emergency odds save failed. Please try again.",
        }),
      );
    } finally {
      setRowSaveLoading((prev) => {
        const { [eventKey]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  useEffect(() => {
    setSelectionMap((previous) => mapSelectionFromEvents(events, previous));
  }, [events]);

  const badges = useMemo(() => deriveBadges(filters), [filters]);

  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
      setIsUpdatingAll(false);
    }
  }, []);

  const fetchStreamResponseStatus = useCallback(async () => {
    try {
      const response = await bufferZoneService.getStatusResponse();
      const status = response?.data?.status ?? null;
      const count = response?.data?.message ?? null;

      if (status && status === "fetching") {
        setStreamMessage("Fetching latest data...");
      } else if (status && status === "running") {
        setStreamMessage(`Processing : ${count} items completed.`);
      } else if (status && status === "finished") {
        setStreamMessage(`Processing : ${count} items completed.`);
        stopStatusPolling();
        setStreamMessage("");
        loadEvents();
        //window.location.reload();
      } else {
        stopStatusPolling();
        loadEvents();
        //window.location.reload();
      }
    } catch (error) {
      console.error("Failed to fetch stream response status", error);
      stopStatusPolling();
      setStreamMessage("Unable to retrieve update status at the moment.");
    }
  }, [stopStatusPolling, loadEvents]);

  const startStatusPolling = useCallback(() => {
    stopStatusPolling();
    fetchStreamResponseStatus();
    statusPollingRef.current = setInterval(fetchStreamResponseStatus, 5000);
  }, [fetchStreamResponseStatus, stopStatusPolling]);

  useEffect(
    () => () => {
      stopStatusPolling();
    },
    [stopStatusPolling],
  );

  return (
    <div className="flex flex-col gap-2">
      {saveLoading && (
        <DotLottieLoading
          type={"MULTIPLE_SAVE"}
          text={"Multiple saving in progress"}
        />
      )}
      {saving && (
        <DotLottieLoading type={"SAVE"} text={"Data saving in progress"} />
      )}
      {isUpdatingAll && (
        <DotLottieLoading type={"UPDATE"} text={streamMessage} />
      )}
      {/* <Alert variant="outlined" severity={message.type} onClose={() => setMessage({text: '', type: 'warning'})}>
        {message.text}
      </Alert> */}
      <BufferZoneHeader
        totalEvents={events.length}
        lastUpdated={logs?.fetch_all ? new Date(logs.fetch_all) : "N/A"}
        refreshing={isUpdatingAll}
        onRefresh={handleUpdateAll}
        emergencyMode={emergencyMode}
      />

      {emergencyMode ? (
        <div className="rounded-2xl border border-customOrange/40 bg-customOrange/10 px-4 py-3 text-sm text-customOrange shadow-sm shadow-customOrange/10 dark:border-customOrange/60 dark:bg-customOrange/20 dark:text-customHeading">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-customOrange text-white">
              <FaExclamationTriangle />
            </span>
            <div>
              <p className="font-bold">Emergency mode is enabled.</p>
              <p className="mt-0.5 text-xs font-semibold text-customOrange/90 dark:text-customHeading/80">
                Automated handicap updates and manual row actions are disabled.
                Please review the events carefully before saving any changes.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {!emergencyMode ? (
        <EventFiltersBar
          searchTerm={filters.search}
          onSearchChange={(value) => updateFilters({ search: value })}
          sortOption={filters.sort}
          onSortChange={(value) => updateFilters({ sort: value })}
          sortOptions={bufferSortingOptions}
          leagueOption={filters.league}
          onLeagueChange={(value) => updateFilters({ league: value })}
          leagueOptions={derived.leagueOptions}
          timeWindow={filters.timeWindow}
          onTimeWindowChange={(value) => updateFilters({ timeWindow: value })}
          timeOptions={bufferTimeOptions}
          advancedFilters={filters.advanced}
          onAdvancedFilterChange={updateAdvancedFilters}
          badges={badges}
          onReset={() => {
            resetFilters();
          }}
        />
      ) : null}
      {duplicateCount > 0 && (
        <div className="rounded-2xl border border-customOrange/40 bg-customOrange/10 px-4 py-3 text-sm text-customOrange dark:border-customOrange/60 dark:bg-customOrange/20 dark:text-customHeading">
          ⚠️ Duplicate events filtered:{" "}
          <span className="font-semibold text-base">{duplicateCount}</span>{" "}
          Total. (⚽Same match with different IDs:{" "}
          <span className="font-semibold text-base">
            {duplicateSummary.sameNameDifferentId}
          </span>
          , ⚽Same match sharing IDs:{" "}
          <span className="font-semibold text-base">
            {duplicateSummary.sameNameSameId}
          </span>
          , ⚽Different matches sharing the same ID:{" "}
          <span className="font-semibold text-base">
            {duplicateSummary.differentNameSameId})
          </span>
          .
        </div>
      )}

      {errorMessage ? (
        <div className="rounded-2xl border border-customOrange/40 bg-customOrange/10 px-4 py-3 text-sm text-customOrange dark:border-customOrange/60 dark:bg-customOrange/20 dark:text-customHeading">
          {errorMessage}
        </div>
      ) : null}

      {streamMessage ? (
        <div className="rounded-2xl flex justify-between border border-customDarkGreen/40 bg-customDarkGreen/10 px-4 py-2 text-sm text-customDarkGreen dark:border-customDarkGreen/60 dark:bg-customDarkGreen/20 dark:text-customHeading">
          <div className="flex gap-2 items-center">
            <div className="h-6 w-8 animate-bounce rounded-full bg-customWhite dark:bg-customWhite flex justify-center items-center">
              <FaCloudDownloadAlt size={25} />
            </div>
            {streamMessage}
          </div>
          <FaRegTimesCircle
            className="text-lg text-customDarkGreen cursor-pointer"
            onClick={() => setStreamMessage("")}
          />
        </div>
      ) : null}

      {isLoading ? (
        <div className="flex min-h-[360px] items-center justify-center rounded-3xl border border-dashed border-customAccent/20 bg-white/80 text-sm font-semibold text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurface">
          <div className="flex items-center gap-3">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-customAccent border-t-transparent" />
            Loading buffer events...
          </div>
        </div>
      ) : emergencyMode ? (
        <EmergencyV0
          events={paginatedEvents}
          allEvents={visibleEvents}
          selectionMap={selectionMap}
          onToggleSelection={handleToggleSelection}
          onSave={handleEmergencySave}
          onSaveMultiple={handleOpenSiteDialog}
          saving={saving}
          pagination={pagination}
          userDetails={userDetails}
          onManualSave={handleEmergencyManualSave}
          rowSaveLoading={rowSaveLoading}
        />
      ) : (
        <div
          className={`${view === "column" ? "grid gap-4 lg:grid-cols-[2fr_1fr] xl:grid-cols-[3fr_1fr]" : "flex flex-col gap-4"}`}
        >
          <EventBufferTable
            events={paginatedEvents}
            selectionMap={selectionMap}
            onToggleSelection={handleToggleSelection}
            onManualUpdate={handleManualUpdate}
            onManualSave={handleManualSave}
            rowFetchLoading={rowFetchLoading}
            rowSaveLoading={rowSaveLoading}
            pagination={pagination}
            lastSyncedAt={logs?.save_manual ? logs.save_manual : null}
            actionsDisabled={emergencyMode}
          />
          <SelectionSummaryPanel
            selectedEvents={selectedEvents}
            onRemove={handleRemoveSelection}
            onSave={() => setSaveDialog(true)}
            saving={saving}
            onReset={handleResetSelections}
            handleOpenSiteDialog={handleOpenSiteDialog}
            view={view}
            setIsView={setIsView}
            userDetails={userDetails}
            logs={logs}
          />
        </div>
      )}
      <SiteSelectionDialog
        open={siteDialogOpen}
        sites={SITE_OPTIONS.filter((site) => !site.lock)}
        selectedSites={siteTargets}
        onToggleSite={handleToggleTargetSite}
        onCancel={handleCancelSiteDialog}
        onSave={handleConfirmSiteDialog}
        isSaving={saving}
        isMobile={onlyWidth < 640}
      />
      <ConfirmationDialog
        isOpen={!!saveDialog}
        setIsOpen={() => setSaveDialog(false)}
        title="Are you sure you want to save betting?"
        contextText={
          "This action will save your selected events to the betting queue. This operation cannot be undone."
        }
        confirmLabel="Save Betting"
        cancelLabel="Cancel"
        handleSubmit={handleSaveSelections}
        appearance="warning"
      />
    </div>
  );
};

export default BufferZonePage;

const siteMapURL = {
  lc7: "https://api.luckyseven.asia",
  mnb: "https://api.moneyball.asia",
  777: "https://api.ll777.asia",
  ep: "https://api.epsoccer.com",
};

